import React from 'react'
import { Box, Typography } from '@mui/material'
import PrimaryStatsValue from './PrimaryStatsValue'
import { StatsValue } from './StatsValue'
import { useDebounce } from 'use-debounce'
import { generateSearchParams } from '@edge/spark/utils'
import type { BasicWidgetProps } from '../../types'
import { formatCardTitle } from '../../utils'
import { WidgetContainer } from '../../WidgetContainer'
import { type SearchCardTypes, useContactLogStatsQuery, useSearchVehicleStatsQuery } from '@edge/spark/io-dashboards'

const BaseWidget: React.VFC<BasicWidgetProps> = ({
    config,
    widgetId,
    handleConfig,
    handleDisplayDetails,
    cardViewMode,
    readonly,
}: BasicWidgetProps) => {
    const searchCardType: SearchCardTypes = config.contactLogSearch ? 'CONTACT_LOG' : 'SEARCH'

    const {
        contactLogSearch,
        workflows,
        search,
        title = 'Custom',
        label,
        label2,
        sublabel,
        linklabel = 'Details',
        linkurl = '/searchdetails/customcard/contact-log',
    } = config

    const { searchString, primary, lowerLeft, lowerRight, searchDetailTitle } = contactLogSearch || search || {}

    const [debounceValue] = useDebounce(searchString || '', 500)

    const { data: contactLogData, loading: contactLogLoading } = useContactLogStatsQuery({
        variables: {
            searchString: debounceValue,
        },
        skip: !searchString || searchCardType !== 'CONTACT_LOG',
    })

    const { data: searchVehicleData, loading: searchVehicleLoading } = useSearchVehicleStatsQuery({
        variables: {
            searchString: debounceValue,
        },
        skip: !searchString || searchCardType !== 'SEARCH',
    })

    if (!contactLogSearch && !search) {
        console.error('Search card must have search configuration')
        console.log('Search card config', config)
        return <div />
    }

    const newUrlParams = generateSearchParams({
        search: searchString,
        shareSubject: formatCardTitle(linklabel),
        title: searchDetailTitle,
        wf: workflows?.toString(),
    })

    const link = `${linkurl}${newUrlParams}`
    const loading = contactLogLoading || searchVehicleLoading
    const contactLogStats = contactLogData?.auction?.search?.contactLogStats
    const searchVehicleStats = searchVehicleData?.auction?.search?.vehicleStats
    const stats = contactLogStats || searchVehicleStats
    const action = { text: linklabel, link }

    return (
        <WidgetContainer
            action={action}
            cardHeaderText={title}
            loading={loading}
            cardConfig={config}
            handleConfig={handleConfig}
            handleDisplayDetails={handleDisplayDetails}
            widgetId={widgetId}
            cardViewMode={cardViewMode}
            readonly={readonly}
        >
            <PrimaryStatsValue searchNumber={primary} stats={stats} />
            {label && (
                <Typography
                    sx={{
                        color: '#616161',
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 'normal',

                        // Single line ellipsis
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',

                        // 2 lines then ellipsis
                        // display: '-webkit-box',
                        // WebkitLineClamp: 2,
                        // WebkitBoxOrient: 'vertical',
                        // overflow: 'hidden'
                    }}
                    align="left"
                >
                    {label}
                </Typography>
            )}
            {label2 && (
                <Typography
                    sx={{
                        color: '#616161',
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 'normal',
                        // Single line ellipsis
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    align="left"
                >
                    {label2}
                </Typography>
            )}
            {sublabel && (
                <Typography
                    sx={{
                        // Single line ellipsis
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    variant="caption"
                    align="left"
                >
                    {sublabel}
                </Typography>
            )}
            <Box display="flex" flexDirection="column" justifyContent="space-between">
                <StatsValue searchNumber={lowerLeft} stats={stats} secondary />
                <StatsValue searchNumber={lowerRight} stats={stats} secondary />
            </Box>
        </WidgetContainer>
    )
}

export default BaseWidget
export type { BasicWidgetProps as BaseWidgetProps }
